import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EditIcon } from "lucide-react";
import { useState } from "react";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";
import { invalidateMatchQueries } from "@/lib/tournament-cache";
import { playerNames } from "@/utils/profiles";
import { isDefined } from "@/utils/types";

import { Radio } from "../base/radio-group";
import { useActiveTeams } from "../tournaments/context";
import type { MatchTeam } from "../tournaments/panels/games/pool-match-grid";

export type SetMatchRefsFormProps = {
  tournamentDivisionId: number;
  opponent?: MatchTeam | null;
  playoffMatchId?: number;
  poolMatchId?: number;
};

export function SetMatchRefsForm(props: SetMatchRefsFormProps) {
  const [isOpen, setOpen] = useState(false);

  return (
    <>
      <Button
        size="sm"
        variant="text"
        className="text-blue-500 hover:text-blue-600"
        onPress={() => setOpen(true)}
        tooltip="Change or set refs"
      >
        <EditIcon size={16} />
      </Button>

      {isOpen && (
        <SetMatchRefsFormContent
          {...props}
          isOpen={isOpen}
          onOpenChange={setOpen}
        />
      )}
    </>
  );
}

function SetMatchRefsFormContent({
  tournamentDivisionId,
  playoffMatchId,
  poolMatchId,
  isOpen,
  onOpenChange,
}: SetMatchRefsFormProps & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: match } = useQuery({
    ...trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
    enabled: isDefined(poolMatchId) || isDefined(playoffMatchId),
  });

  const matchTeams = (match ? [match.teamAId, match.teamBId] : []).filter(
    isDefined,
  );

  const teams = useActiveTeams(tournamentDivisionId);

  const teamOptions = teams
    ?.map(({ id, team }) => ({
      value: id,
      display: playerNames(team.players.map(({ profile }) => profile)).join(
        " & ",
      ),
    }))
    .filter(({ value }) => !matchTeams.includes(value));

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.setMatchRefs.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(
        trpc.matches.get.queryOptions({ poolMatchId, playoffMatchId }),
      );
      invalidateMatchQueries(queryClient, trpc);

      onOpenChange(false);
    },
  });

  const schema = z
    .object({
      teamId: z.number().nullable().optional(),
      profileIds: z.array(z.number()),
    })
    .extend({
      picker: z.enum(["team", "profile"]),
    });

  const form = useAppForm({
    defaultValues: {
      teamId: null,
      profileIds: [],
      picker: "team",
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    listeners: {
      onChange: ({ formApi, fieldApi }) => {
        if (fieldApi.name.startsWith("profileIds")) {
          const current = formApi.getFieldValue("profileIds");
          const filtered = current.filter(isDefined);
          if (filtered.length !== current.length) {
            formApi.setFieldValue("profileIds", filtered);
          }
        }
      },
    },
    onSubmit: ({ value: { profileIds, teamId }, formApi }) => {
      mutate(
        {
          poolMatchId,
          playoffMatchId,
          profileIds,
          teamId,
        },
        {
          onSuccess: () => {
            formApi.reset();
          },
        },
      );
    },
  });

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Assign Referee Duties
        </Heading>

        <p>
          Assign reffing duties. This action removes any existing refs for this
          match.
        </p>

        <form
          className="flex flex-col space-y-3"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to create pools"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField name="picker">
            {(field) => (
              <field.RadioGroup
                field={field}
                orientation="horizontal"
                className="mb-2"
              >
                <Radio value="team">Select a team</Radio>
                <Radio value="profile">Search profiles</Radio>
              </field.RadioGroup>
            )}
          </form.AppField>

          <form.Subscribe selector={(state) => state.values.picker}>
            {(picker) =>
              picker === "team" ? (
                <form.AppField name="teamId">
                  {(field) => (
                    <field.Select
                      label="Select team"
                      field={field}
                      options={teamOptions ?? []}
                    />
                  )}
                </form.AppField>
              ) : (
                <form.AppField
                  name="profileIds"
                  mode="array"
                  children={(field) => (
                    <>
                      <form.AppField
                        name={`profileIds[${field.state.value?.length ?? 0}]`}
                      >
                        {(subField) => (
                          <subField.ProfilePicker
                            label="Search Profiles"
                            field={subField}
                            selectedProfileIds={field.state.value?.filter(
                              isDefined,
                            )}
                          />
                        )}
                      </form.AppField>

                      {field.state.value?.map((_, i) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: form array fields keyed by position
                        <form.AppField key={i} name={`profileIds[${i}]`}>
                          {(subField) => (
                            <subField.ProfilePicker
                              label="Search Profiles"
                              field={subField}
                              selectedProfileIds={field.state.value.filter(
                                isDefined,
                              )}
                            />
                          )}
                        </form.AppField>
                      ))}
                    </>
                  )}
                />
              )
            }
          </form.Subscribe>

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton>Submit</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
